import dayjs from '@/lib/dayjs'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import type {
  SalesOrderFulfillmentStatus as FulfillmentStatus,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import type { Dayjs } from 'dayjs'

/** The order fields a helper reads: a full order and an index row both carry them. */
type OrderFields<K extends keyof SalesOrderListItem> = Pick<SalesOrderListItem, K>

/** An order and the status a fulfillment action would move it to. */
export interface FulfillmentNotificationCheck {
  order: OrderFields<'fulfillment_status' | 'fulfilled_at' | 'fulfillment_type'>
  nextFulfillmentStatus: FulfillmentStatus
}

/** Also compares the order's due date with today (`now`, by default the current time). */
export interface DatedFulfillmentNotificationCheck extends FulfillmentNotificationCheck {
  order: FulfillmentNotificationCheck['order'] &
    OrderFields<
      | 'fulfillment_due_date'
      | 'customer_fulfillment_notified'
      | 'customer_ready_for_pickup_notified'
    >
  now?: Dayjs
}

function isFulfilledOrder(order: OrderFields<'fulfillment_status' | 'fulfilled_at'>): boolean {
  return (
    order?.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value ||
    Boolean(order?.fulfilled_at)
  )
}

function isReadyForPickupOrder(order: OrderFields<'fulfillment_status'>): boolean {
  return order?.fulfillment_status === SalesOrderFulfillmentStatus.ReadyForPickup.Value
}

export function isDeliveryFulfillmentAction({
  order,
  nextFulfillmentStatus,
}: FulfillmentNotificationCheck): boolean {
  return (
    order?.fulfillment_type === FulfillmentType.Delivery.Value &&
    !isFulfilledOrder(order) &&
    nextFulfillmentStatus === SalesOrderFulfillmentStatus.Fulfilled.Value
  )
}

export function isPickupReadyAction({
  order,
  nextFulfillmentStatus,
}: FulfillmentNotificationCheck): boolean {
  return (
    order?.fulfillment_type === FulfillmentType.Pickup.Value &&
    !isFulfilledOrder(order) &&
    !isReadyForPickupOrder(order) &&
    nextFulfillmentStatus === SalesOrderFulfillmentStatus.ReadyForPickup.Value
  )
}

export function isPickupPickedUpAction({
  order,
  nextFulfillmentStatus,
}: FulfillmentNotificationCheck): boolean {
  return (
    order?.fulfillment_type === FulfillmentType.Pickup.Value &&
    !isFulfilledOrder(order) &&
    nextFulfillmentStatus === SalesOrderFulfillmentStatus.Fulfilled.Value
  )
}

export function isCustomerFulfillmentNotificationAction({
  order,
  nextFulfillmentStatus,
}: FulfillmentNotificationCheck): boolean {
  return (
    isDeliveryFulfillmentAction({ order, nextFulfillmentStatus }) ||
    isPickupReadyAction({ order, nextFulfillmentStatus }) ||
    isPickupPickedUpAction({ order, nextFulfillmentStatus })
  )
}

export function sourceAllowsCustomerFulfillmentNotifications(
  order: OrderFields<'source_allows_customer_fulfillment_notifications'>
): boolean {
  return order?.source_allows_customer_fulfillment_notifications !== false
}

function isFulfillmentDueDateBeforeToday({
  order,
  now = dayjs(),
}: {
  order: OrderFields<'fulfillment_due_date'>
  now?: Dayjs
}): boolean {
  if (!order?.fulfillment_due_date) {
    return false
  }

  return dayjs(order.fulfillment_due_date).startOf('day').isBefore(now.startOf('day'), 'day')
}

export function isOverdueDeliveryFulfillment({
  order,
  nextFulfillmentStatus,
  now = dayjs(),
}: DatedFulfillmentNotificationCheck): boolean {
  if (isFulfilledOrder(order)) {
    return false
  }

  if (!isDeliveryFulfillmentAction({ order, nextFulfillmentStatus })) {
    return false
  }

  return isFulfillmentDueDateBeforeToday({ order, now })
}

export function isLatePickupFulfillment({
  order,
  nextFulfillmentStatus,
  now = dayjs(),
}: DatedFulfillmentNotificationCheck): boolean {
  if (isFulfilledOrder(order)) {
    return false
  }

  if (!isPickupPickedUpAction({ order, nextFulfillmentStatus })) {
    return false
  }

  return isFulfillmentDueDateBeforeToday({ order, now })
}

export function defaultShouldNotifyCustomer({
  order,
  nextFulfillmentStatus,
  now = dayjs(),
}: DatedFulfillmentNotificationCheck): boolean {
  // Marking a delivered or picked-up order not delivered or not picked up: correct the customer only if they were told
  // it was.
  if (
    isFulfilledOrder(order) &&
    nextFulfillmentStatus !== SalesOrderFulfillmentStatus.Fulfilled.Value
  ) {
    return Boolean(order.customer_fulfillment_notified)
  }

  if (!isCustomerFulfillmentNotificationAction({ order, nextFulfillmentStatus })) {
    return false
  }

  // An order moved back and fulfilled again: the customer already heard it was delivered or picked up.
  if (
    nextFulfillmentStatus === SalesOrderFulfillmentStatus.Fulfilled.Value &&
    order.customer_fulfillment_notified
  ) {
    return false
  }

  // An order moved back and made ready again: the customer already heard it was ready.
  if (
    nextFulfillmentStatus === SalesOrderFulfillmentStatus.ReadyForPickup.Value &&
    order.customer_ready_for_pickup_notified
  ) {
    return false
  }

  return (
    !isOverdueDeliveryFulfillment({ order, nextFulfillmentStatus, now }) &&
    !isLatePickupFulfillment({ order, nextFulfillmentStatus, now })
  )
}
